import {
  Box,
  Button,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconHistory } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import HistoryModal from "src/components/shared/HistoryModal/HistoryModal";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import { useGetApi, useIsOnline } from "src/hooks";
import {
  type IFgasGwpItem,
  type IFgasLegalStatus,
} from "src/pages/Settings/YachtSetup/Fgas/Fgas.types";
import {
  gwpFields,
  legalStatusFields,
  mapFgasHistoryRow,
  mapGwpSnapshot,
  mapLegalStatusSnapshot,
} from "src/pages/Settings/YachtSetup/Fgas/Fgas.utils";
import { getYachtId } from "src/utils";

import styles from "../YachtSetUp.module.scss";
import FgasGwpTableHead from "./FgasGwpTableHead";

interface Props {
  type: "legalStatus" | "gwp";
}

const config = {
  legalStatus: {
    title: "F-GAS Legal Status",
    apiUrl: "get-fgas-legal-status-hist",
  },
  gwp: { title: "F-GAS GWP", apiUrl: "get-fgas-gwp-hist" },
};

const formatExpiryDate = (value: IFgasLegalStatus["expiry_date"]) => {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "";
};

const formatSnapshotLabel = (row: any) => {
  const date = dayjs(row?.date);
  return [date.isValid() && date.format("D MMM YYYY - HH:mm"), row?.detail]
    .filter(Boolean)
    .join(", ");
};

const LegalStatusSnapshot = ({ item }: { item: IFgasLegalStatus }) => (
  <Stack>
    {legalStatusFields.map(field =>
      field.multiline ? (
        <Textarea
          key={field.name}
          autosize
          minRows={4}
          label={field.label}
          value={item[field.name] ?? ""}
          readOnly
        />
      ) : (
        <TextInput
          key={field.name}
          label={field.label}
          value={item[field.name] ?? ""}
          readOnly
        />
      ),
    )}
    <TextInput
      label="Expiry Date"
      value={formatExpiryDate(item.expiry_date)}
      readOnly
    />
  </Stack>
);

const GwpSnapshot = ({ items }: { items: IFgasGwpItem[] }) => (
  <Box
    style={{
      border: "1px solid var(--mantine-color-settingsColor-0)",
      borderRadius: 5,
      overflow: "hidden",
    }}>
    <Table.ScrollContainer
      type="native"
      minWidth={700}
      mah="60vh"
      style={{ overflowY: "auto" }}>
      <Table
        stickyHeader
        striped
        className={styles.stickyBorderTable}
        withColumnBorders
        verticalSpacing="sm"
        borderColor="var(--mantine-color-settingsColor-0)">
        <FgasGwpTableHead />
        <Table.Tbody>
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={gwpFields.length}>
                <Text c="dimmed" ta="center" size="sm">
                  No GWP items.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {items.map((item, index) => (
            <Table.Tr key={item.id ?? index}>
              {gwpFields.map((field, fieldIndex) => (
                <Table.Td
                  key={field.name}
                  ta={fieldIndex < 3 ? "left" : "center"}
                  fw={fieldIndex === 0 ? 600 : undefined}>
                  {item[field.name] || "-"}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  </Box>
);

const FgasHistory = ({ type }: Props) => {
  const isOnline = useIsOnline();
  const [historyOpened, { open: openHistory, close: closeHistory }] =
    useDisclosure(false);
  const [viewOpened, { open: openView, close: closeView }] =
    useDisclosure(false);
  const [snapshot, setSnapshot] = useState<any>(null);

  const { data: users, refetch: fetchUsers } = useGetApi<{ list: IUser[] }>({
    request: "get-users-full",
    body: { params: { yachtid: getYachtId() } },
    fetchOnInit: false,
  });

  const { title, apiUrl } = config[type];

  const handleOpenHistory = () => {
    if (!users) void fetchUsers();
    openHistory();
  };

  const handleView = (row: any) => {
    setSnapshot(row);
    openView();
  };

  if (!isOnline) return null;

  return (
    <>
      <Button
        onClick={handleOpenHistory}
        variant="outline"
        leftSection={<IconHistory size="1.4rem" />}>
        History
      </Button>
      <HistoryModal
        opened={historyOpened}
        close={closeHistory}
        elementName={title}
        apiUrl={apiUrl}
        params={{ yachtid: getYachtId() }}
        onView={handleView}
        mapRow={mapFgasHistoryRow(users?.list ?? [])}
      />
      <Modal
        size={type === "gwp" ? "xl" : "lg"}
        zIndex={201}
        opened={viewOpened}
        onClose={closeView}
        title={
          <Stack gap={0}>
            <span>{title} History</span>
            <Text fw={700} size="sm">
              {formatSnapshotLabel(snapshot)}
            </Text>
          </Stack>
        }>
        {type === "gwp" ? (
          <GwpSnapshot items={mapGwpSnapshot(snapshot)} />
        ) : (
          <LegalStatusSnapshot item={mapLegalStatusSnapshot(snapshot)} />
        )}
      </Modal>
    </>
  );
};

export default FgasHistory;
